import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { environment } from '../../../environments/environment';

const SOCKET_MANAGER_NAMESPACE = '/socket';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private sockets: Map<string, Socket> = new Map<string, Socket>();
  private messageRoomSubject: Subject<any> = new Subject<any>();
  private messageNSPSubject: Subject<any> = new Subject<any>();
  private socketConnectedSubject: Subject<string> = new Subject<string>();

  private readonly channelKeys: Record<string, string> = {
    [environment.ROOM_SOCKET_CW]: environment.SOCKET_MANAGER_CHANNEL_CW_KEY,
    [environment.ROOM_SOCKET_FN]: environment.SOCKET_MANAGER_CHANNEL_FINANCIAL_KEY,
    [environment.ROOM_SOCKET_TICKETS]: environment.SOCKET_MANAGER_CHANNEL_TICKETS_KEY,
    [environment.ROOM_SOCKET_BOT_EXTRACT]: environment.SOCKET_MANAGER_CHANNEL_EXTRACT_CW_KEY,
    [environment.ROOM_SOCKET_BOT_LIVE]: environment.SOCKET_MANAGER_CHANNEL_EXTRACT_CW_KEY,
  };

  connectToNamespace(_namespace: string): void {
    this.ensureConnection(environment.ROOM_SOCKET_CW);
    this.ensureConnection(environment.ROOM_SOCKET_FN);
  }

  getMessageNSPSubject(): Subject<any> {
    return this.messageNSPSubject;
  }

  getMessageRoomSubject(): Subject<any> {
    return this.messageRoomSubject;
  }

  getSocketConnectedSubject(): Subject<string> {
    return this.socketConnectedSubject;
  }

  joinRoom(room: string): void {
    this.ensureConnection(room);
  }

  leaveRoom(room: string): void {
    const socket = this.sockets.get(room);
    if (socket) {
      socket.disconnect();
      this.sockets.delete(room);
    }
  }

  disconnectFromNamespace(): void {
    this.sockets.forEach((socket) => socket.disconnect());
    this.sockets.clear();
  }

  private ensureConnection(room: string): void {
    if (this.sockets.has(room)) {
      return;
    }

    const apiKey = this.channelKeys[room];
    if (!apiKey) {
      console.error(`SocketService: no hay canal de Socket Manager configurado para "${room}"`);
      return;
    }

    // SOCKET_MANAGER_URL puede venir con o sin barra final (ej.
    // 'https://host.com/' vs 'https://host.com') — si viniera con barra,
    // concatenar directo con SOCKET_MANAGER_NAMESPACE ('/socket') generaba
    // 'https://host.com//socket' (doble barra). El transporte WS/engine.io
    // conecta igual (no le importa el namespace todavía), pero la conexión
    // al namespace real nunca llega a SocketGateway del lado del servidor.
    const baseUrl = environment.SOCKET_MANAGER_URL.replace(/\/+$/, '');

    // transports: ['websocket'] — conecta directo por WS, sin el paso previo
    // de polling ni el upgrade posterior (polling -> websocket, misma sesión).
    // Confirmado con curl real: un WS nuevo de cero conecta perfecto; la
    // transición de una sesión de polling existente a WS se corta en algún
    // punto de la cadena de proxies (Cloudflare enrutando esa continuación a
    // un borde distinto del que abrió la sesión de polling).
    const socket = io(`${baseUrl}${SOCKET_MANAGER_NAMESPACE}`, {
      auth: { apiKey },
      transports: ['websocket'],
    });

    socket.onAny((_event: string, payload: any) => {
      if (!payload || typeof payload !== 'object') {
        return;
      }
      if (payload.kind === 'namespace') {
        this.messageNSPSubject.next({
          origin: payload.origin,
          action: payload.action,
          data: payload.data,
        });
      } else if (payload.kind === 'room') {
        this.messageRoomSubject.next({
          action: payload.action,
          data: payload.data,
        });
      }
    });

    socket.on('connect', () => {
      this.socketConnectedSubject.next(room);
    });

    socket.on('disconnect', () => {
      console.log(`Disconnected from channel: ${room}`);
    });

    this.sockets.set(room, socket);
  }
}
